import { ApolloError, useQuery } from '@apollo/client';
import { useState } from 'react';
import { useDebounce } from 'usehooks-ts';

import { CandidateInvitationBatchState } from '@/__generated__/graphql';
import ERROR_CODES from '@/core/apiErrorCodes';

import {
  type ClaudeDraftState,
  type EmailDraft,
  type EmailEditorState,
  INVITATION_BODY_MAX_LENGTH,
  INVITATION_SUBJECT_MAX_LENGTH,
} from './EmailEditor';
import { type InvitationBatch, displayState } from './InvitationBatchView';
import { PREVIEW_CANDIDATE_INVITATION_EMAIL } from './operations';

// Long enough that typing doesn't render the email on every key.
const PREVIEW_DEBOUNCE_MS = 400;

/** The parts of the draft that differ from the standard email: what Send carries. */
export interface EmailEdit {
  subject?: string;
  body?: string;
}

function sameEmail(a: EmailDraft, b: EmailDraft): boolean {
  return a.subject === b.subject && a.body === b.body;
}

function editOf(draft: EmailDraft, template: EmailDraft): EmailEdit {
  const edit: EmailEdit = {};
  if (draft.subject !== template.subject) edit.subject = draft.subject;
  if (draft.body !== template.body) edit.body = draft.body;
  return edit;
}

// Where the editor's draft stands against the one Claude wrote at prepare.
function claudeDraftState(
  current: EmailDraft,
  claude: EmailDraft,
  standard: EmailDraft
): ClaudeDraftState {
  if (sameEmail(current, claude)) return 'unchanged';
  if (sameEmail(current, standard)) return 'standard';
  return 'edited';
}

// The checks the page can make itself. The api makes these and the rest (the
// fields an email may name, the opportunity link) on every preview and on Send.
function draftProblem({ subject, body }: EmailDraft): string | null {
  if (!subject.trim()) return 'Add a subject.';
  if (subject.length > INVITATION_SUBJECT_MAX_LENGTH) {
    return `Keep the subject to ${INVITATION_SUBJECT_MAX_LENGTH} characters.`;
  }
  if (!body.trim()) return 'Add a body.';
  if (body.length > INVITATION_BODY_MAX_LENGTH) {
    return `Keep the body to ${INVITATION_BODY_MAX_LENGTH.toLocaleString('en-US')} characters.`;
  }
  return null;
}

/**
 * The api's reason an edit of the email can't be sent, or null when err is not
 * that refusal.
 */
export function invalidEmailReason(err: unknown): string | null {
  if (!(err instanceof ApolloError)) return null;
  const refusal = err.graphQLErrors.find(
    (e) => e.extensions?.code === ERROR_CODES.INVITATION_BATCH_INVALID_EMAIL
  );
  return refusal ? `This email can't be sent: ${refusal.message}.` : null;
}

interface InvitationDraft {
  /** The editor's state; undefined when the batch can't be edited. */
  editor?: EmailEditorState;
  /** What Send carries: nothing when the preparer changed nothing. */
  edit: EmailEdit;
  /**
   * Records the api's reason a Send refused this draft, so Send stays disabled
   * for it until the subject or body changes.
   */
  refuseSend: (reason: string) => void;
}

// A Send the api refused for the copy, and the draft it carried.
interface SendRefusal {
  draft: EmailDraft;
  reason: string;
}

/**
 * The Send screen's draft of the invitation (ENG-6310). It starts as the
 * draft Claude wrote when it prepared the batch (email_draft, ENG-6315), or
 * else the batch's email_template, the standard email, and is kept here until
 * Send, which carries only the parts that differ from the standard email: a
 * draft the preparer leaves as it is goes out as their edit, and a reset sends
 * the standard email. While the preparer edits or picks another expert, the
 * api renders the draft for that expert and checks it the way a send would;
 * nothing is stored until Send.
 */
export function useInvitationDraft(
  token: string,
  batch: InvitationBatch | undefined
): InvitationDraft {
  const [draft, setDraft] = useState<EmailDraft | null>(null);
  const [pickedProfileId, setPickedProfileId] = useState<string | null>(null);
  const [sendRefusal, setSendRefusal] = useState<SendRefusal | null>(null);

  // Null until the preparer edits, so the email the editor starts from needs no
  // debounce when the batch loads. A state value, so its identity changes only
  // on edit.
  const debouncedDraft = useDebounce(draft, PREVIEW_DEBOUNCE_MS);

  const template = batch?.email_template;
  const standard = { subject: template?.subject ?? '', body: template?.body ?? '' };
  const claudeDraft = batch?.email_draft
    ? { subject: batch.email_draft.subject, body: batch.email_draft.body }
    : null;
  const initial = claudeDraft ?? standard;
  const current = draft ?? initial;
  const debounced = debouncedDraft ?? initial;

  const recipients = batch?.recipients ?? [];
  const firstProfileId = recipients[0]?.profile_id ?? '';
  const previewProfileId =
    pickedProfileId && recipients.some((r) => r.profile_id === pickedProfileId)
      ? pickedProfileId
      : firstProfileId;

  const editable =
    !!batch &&
    !!template &&
    recipients.length > 0 &&
    displayState(batch) === CandidateInvitationBatchState.Pending;
  const edit = editOf(current, standard);
  const debouncedEdit = editOf(debounced, standard);
  const localProblem = draftProblem(current);
  // The batch's own email is already the standard email for the first
  // recipient; anything else is rendered by the api.
  const needsRender =
    editable && (Object.keys(debouncedEdit).length > 0 || previewProfileId !== firstProfileId);

  const rendered = useQuery(PREVIEW_CANDIDATE_INVITATION_EMAIL, {
    variables: { token, profileId: previewProfileId, ...debouncedEdit },
    skip: !needsRender || !!draftProblem(debounced),
  });

  const refuseSend = (reason: string) => setSendRefusal({ draft: current, reason });
  if (!editable || !batch) return { edit: {}, refuseSend };

  const refusal = invalidEmailReason(rendered.error);
  // A refused Send holds only for the draft it carried; any change clears it.
  const refusedAtSend =
    sendRefusal && sameEmail(sendRefusal.draft, current) ? sendRefusal.reason : null;
  const problem = localProblem ?? refusedAtSend ?? refusal;
  const renderedEmail =
    rendered.data?.candidateInvitationBatch?.email ??
    rendered.previousData?.candidateInvitationBatch?.email;
  const settled = draft === debouncedDraft;
  // Until the first render lands, the batch's own email stands in, dimmed. It
  // is the standard email, so not for Claude's draft before its first render.
  const standIn = needsRender && claudeDraft ? null : batch.email;

  return {
    edit,
    refuseSend,
    editor: {
      draft: current,
      edited: Object.keys(edit).length > 0,
      claudeDraft: claudeDraft ? claudeDraftState(current, claudeDraft, standard) : null,
      problem,
      preview: {
        email: problem ? null : ((needsRender ? renderedEmail : undefined) ?? standIn),
        updating: !settled || (needsRender && rendered.loading),
        failed: !!rendered.error && !refusal,
      },
      previewProfileId,
      onChange: setDraft,
      // Null is where the editor started, so it is the standard email only
      // when Claude drafted none.
      onReset: () => setDraft(claudeDraft ? standard : null),
      onRestoreClaudeDraft: () => setDraft(null),
      onPreviewProfileChange: setPickedProfileId,
    },
  };
}
